/**
 * URL detection and linkification utilities
 */

// Improved URL regex that properly handles underscores, hyphens, and other valid URL characters
const COMPREHENSIVE_URL_REGEX =
	/((?:https?:\/\/)?(?:www\.)?(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}|localhost(?::[0-9]+)?|(?:[0-9]{1,3}\.){3}[0-9]{1,3}(?::[0-9]+)?)(?:\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]*)?)/gi;

/**
 * Decode HTML entities in text
 */
const decodeHtmlEntities = (text: string): string => {
	const textarea = document.createElement('textarea');
	textarea.innerHTML = text;
	return textarea.value;
};

/**
 * Detect if a string contains URLs
 */
export const containsUrls = (text: string): boolean => {
	// Test both original text and decoded version
	return (
		COMPREHENSIVE_URL_REGEX.test(text) || COMPREHENSIVE_URL_REGEX.test(decodeHtmlEntities(text))
	);
};

/**
 * Extract all URLs from a text string
 */
export const extractUrls = (text: string): string[] => {
	const matches = text.match(COMPREHENSIVE_URL_REGEX);
	return matches || [];
};

/**
 * Check if a string is a valid URL
 */
export const isValidUrl = (string: string): boolean => {
	try {
		// Add protocol if missing for URL constructor
		const urlString = string.startsWith('http') ? string : `https://${string}`;
		new URL(urlString);
		return true;
	} catch (_) {
		return false;
	}
};

/**
 * Ensure URL has a protocol (adds https:// if missing)
 */
export const ensureProtocol = (url: string): string => {
	if (url.startsWith('http://') || url.startsWith('https://')) {
		return url;
	}
	return `https://${url}`;
};

/**
 * Convert plain text URLs to HTML links
 */
export const linkifyText = (text: string): string => {
	// First decode HTML entities to work with actual URLs
	const decodedText = decodeHtmlEntities(text);

	return decodedText.replace(COMPREHENSIVE_URL_REGEX, (url) => {
		const href = ensureProtocol(url);
		const displayUrl = url.length > 50 ? `${url.substring(0, 47)}...` : url;
		return `<a href="${href}" target="_blank" rel="noopener noreferrer">${displayUrl}</a>`;
	});
};

/**
 * Convert HTML content with URLs to linkified HTML
 * This function processes HTML content and converts plain text URLs to links
 * while preserving existing HTML structure
 */
export const linkifyHtmlContent = (html: string): string => {
	if (!html) {
		return html;
	}

	// Handle HTML entities directly in the string
	// Replace URLs in HTML content, handling both & and &amp;
	const urlRegexWithEntities =
		/((?:https?:\/\/)?(?:www\.)?(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}|localhost(?::[0-9]+)?|(?:[0-9]{1,3}\.){3}[0-9]{1,3}(?::[0-9]+)?)(?:\/[^\s<>"]*(?:&amp;|&)[^\s<>"]*)*)/gi;

	// Check if there are URLs to process
	if (!urlRegexWithEntities.test(html)) {
		return html;
	}

	// Reset regex lastIndex
	urlRegexWithEntities.lastIndex = 0;

	// Replace URLs with links, preserving HTML entities in href but using decoded version for display
	return html.replace(urlRegexWithEntities, (match) => {
		// Decode the URL for the href attribute
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = match;
		const decodedUrl = tempDiv.textContent || tempDiv.innerText || match;

		const href = ensureProtocol(decodedUrl);
		const displayUrl = decodedUrl.length > 50 ? `${decodedUrl.substring(0, 47)}...` : decodedUrl;

		return `<a href="${href}" target="_blank" rel="noopener noreferrer">${displayUrl}</a>`;
	});
};

/**
 * Auto-linkify URLs in rich text editor content
 * This is specifically designed for ReactQuill content
 */
export const autoLinkifyQuillContent = (html: string): string => {
	// Don't process if empty
	if (!html || html === '<p><br></p>' || html === '<p></p>') {
		return html;
	}

	// Create a temporary div to parse the HTML
	const tempDiv = document.createElement('div');
	tempDiv.innerHTML = html;

	// Function to process text nodes and linkify URLs
	const processTextNodes = (node: Node): void => {
		if (node.nodeType === Node.TEXT_NODE) {
			const text = node.textContent || '';
			if (containsUrls(text)) {
				// Create a temporary element to hold the linkified content
				const tempSpan = document.createElement('span');
				tempSpan.innerHTML = linkifyText(text);

				// Replace the text node with the linkified content
				const parent = node.parentNode;
				if (parent) {
					// Insert all new nodes
					while (tempSpan.firstChild) {
						parent.insertBefore(tempSpan.firstChild, node);
					}
					// Remove the original text node
					parent.removeChild(node);
				}
			}
		} else if (node.nodeType === Node.ELEMENT_NODE) {
			// Don't process existing links
			if (node.nodeName.toLowerCase() !== 'a') {
				// Process child nodes (convert to array to avoid live NodeList issues)
				const children = Array.from(node.childNodes);
				children.forEach(processTextNodes);
			}
		}
	};

	// Process all nodes
	Array.from(tempDiv.childNodes).forEach(processTextNodes);

	return tempDiv.innerHTML;
};

/**
 * Extract domain from URL for display purposes
 */
export const extractDomain = (url: string): string => {
	try {
		const urlObj = new URL(ensureProtocol(url));
		return urlObj.hostname.replace(/^www\./, '');
	} catch (_) {
		return url;
	}
};

/**
 * Check if URL is an image URL based on file extension
 */
export const isImageUrl = (url: string): boolean => {
	const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
	const lowercaseUrl = url.toLowerCase();
	return imageExtensions.some((ext) => lowercaseUrl.includes(ext));
};

/**
 * Check if URL is a video URL based on file extension or domain
 */
export const isVideoUrl = (url: string): boolean => {
	const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov'];
	const videoDomains = ['youtube.com', 'youtu.be', 'vimeo.com', 'twitch.tv'];
	const lowercaseUrl = url.toLowerCase();

	return (
		videoExtensions.some((ext) => lowercaseUrl.includes(ext)) ||
		videoDomains.some((domain) => lowercaseUrl.includes(domain))
	);
};
